// ./games/crash/styles/BetBox.styles.ts
import React from 'react';
import { Theme } from '@config/themes/ITheme';
import type { DeviceType } from '@platform/contracts';

// Helper for the Toggle Switch styles which are used in auto-bet section
export const toggleStyles = {
  container: {
    width: '100%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    boxSizing: 'border-box' as React.CSSProperties['boxSizing'],
  } as React.CSSProperties, // Using vh for height to ensure responsiveness (kept from the first file)
  switch: {
    display: 'flex',
    alignItems: 'center',
    cursor: 'pointer',
    position: 'relative',
    userSelect: 'none',
    padding: '4px',
    width: '36px',
    height: '24px',
    borderRadius: '32px',
    backgroundColor: '#141516',
  } as React.CSSProperties,
  slider: {
    position: 'absolute',
    cursor: 'pointer',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    transition: '.3s',
    borderRadius: 20,
  } as React.CSSProperties,
  slidderInput: {
    minWidth: '16px',
    maxWidth: '16px',
    height: '16px', // backgroundColor: '#a3a3a3',
    borderRadius: '50%',
    backgroundColor: '#2c2d30',
  } as React.CSSProperties,
  sliderBefore: {
    position: 'absolute',
    content: '""',
    height: '2.2vh', // Converted to vh
    width: '2.2vh', // Converted to vh
    left: 3,
    bottom: 3,
    backgroundColor: '#fff',
    transition: '.3s',
    borderRadius: '50%',
  } as React.CSSProperties,
  input: {
    opacity: 0,
    width: 0,
    height: 0,
  } as React.CSSProperties,
};
// Global style for secondary text
export const subtextStyle: React.CSSProperties = {
  fontSize: 14,
  fontWeight: 500,
  marginTop: 8,
  opacity: 0.75,
  letterSpacing: '0.4px',
  textAlign: 'center',
  userSelect: 'none',
};

export const getBetBoxStyles = (
  theme: Theme,
  deviceType: DeviceType,
  isLandscape: boolean,
  isIpad: boolean,
  uiProfile?: string
): Record<string, React.CSSProperties> => {
  const usesCompactPurpleProfile = uiProfile === 'compactPurple';
  const uiFont = usesCompactPurpleProfile
    ? "'Roboto', sans-serif"
    : 'Inter, sans-serif';
  const inputFont = usesCompactPurpleProfile
    ? "'Roboto', sans-serif"
    : '"Inter", "Roboto Mono", "Consolas", monospace';

  const panelBg = usesCompactPurpleProfile
    ? theme.colors.background
    : '#1b1c1d';
  const frameBg = usesCompactPurpleProfile ? '#0e0430' : '#141516';
  const frameStroke = usesCompactPurpleProfile ? '#a13ef8' : '#141516';
  const tabActiveBg = usesCompactPurpleProfile
    ? 'linear-gradient(180deg, #680bb9 100%, #a13ef8 0%)'
    : '#2c2d30';
  const tabInactiveColor = usesCompactPurpleProfile
    ? '#ffffff'
    : theme.colors.background4;
  const stakeBtnBg = usesCompactPurpleProfile ? '#5c2bc6' : '#2c2d30';
  const quickBetBg = usesCompactPurpleProfile
    ? 'linear-gradient(180deg, #5a2ab8 0%, #4b1d9e 100%)'
    : '#141516';
  const quickBetText = usesCompactPurpleProfile ? '#ffffff' : '#ffffff80';
  const quickBetBorder = usesCompactPurpleProfile ? '#9a65f7' : 'transparent';
  const autoPlayPillWidth = '70px';
  const autoPlayPillBorderRadius = '16px';
  const quickBetChipBorderRadius = usesCompactPurpleProfile ? '999px' : '64px';
  const quickBetChipPadding = deviceType === 'mobile' ? '5px 9px' : '6px 12px';
  const quickBetChipFontSize = deviceType === 'mobile' ? 12 : 13;
  const autoPlayPillFontSize = '10px';
  const autoPlayPillMinHeight =
    deviceType === 'mobile'
      ? '3.0vh'
      : deviceType === 'tablet' ||
          (deviceType === 'desktop' && isIpad && !isLandscape)
        ? '2.3vw'
        : isLandscape
          ? '4.3vh'
          : '3.3vh';
  const quickBetGridGap = usesCompactPurpleProfile
    ? deviceType === 'mobile'
      ? 5
      : 6
    : 4;
  const stakeBtnSizePxForBlockWidth = 30;
  const stakeInputGapPxForBlockWidth = 4;
  const stakeInputMaxWidthPxForBlockWidth = 100;
  const stakeInputWrapPaddingPxForBlockWidth = 4;
  const quickBetGridWidth = usesCompactPurpleProfile
    ? `${stakeBtnSizePxForBlockWidth * 2 + stakeInputGapPxForBlockWidth * 2 + stakeInputMaxWidthPxForBlockWidth + stakeInputWrapPaddingPxForBlockWidth}px`
    : undefined;
  /** Stake field + quick-bet labels share the same typography (compact purple / SkyX). */
  const stakeAmountTextStyle: React.CSSProperties = usesCompactPurpleProfile
    ? {
        fontFamily: inputFont,
        fontWeight: 800,
        fontSize: 13,
        color: '#ffffff',
        textRendering: 'geometricPrecision',
        fontVariantNumeric: 'proportional-nums',
        lineHeight: 1,
        transform: 'scaleX(1.0)',
        WebkitFontSmoothing: 'antialiased',
        MozOsxFontSmoothing: 'grayscale',
      }
    : {};

  return {
    betBoxControl: {
      display: 'flex',
      flexDirection: 'column',
      position: 'relative', // This is required for the overlay
      width: '100%',
      justifyContent: usesCompactPurpleProfile ? 'flex-start' : 'center',
      alignItems: 'center',
      borderRadius: usesCompactPurpleProfile ? '12px' : '20px',
      backgroundColor: panelBg,
      border: usesCompactPurpleProfile ? '1px solid #3c2193' : 'none',
      boxShadow: usesCompactPurpleProfile
        ? 'inset 0 1px 0 rgba(255,255,255,0.05), inset 0 -1px 0 rgba(0,0,0,0.32)'
        : 'none',
      height: usesCompactPurpleProfile ? '126px' : '184px',
      marginBottom: deviceType === 'mobile' ? '3px' : '0px', // margin: '0 auto',
      minHeight: usesCompactPurpleProfile
        ? deviceType === 'desktop'
          ? '126px'
          : '124px'
        : deviceType === 'desktop'
          ? '144px'
          : '128px',
      marginTop: usesCompactPurpleProfile
        ? deviceType === 'desktop'
          ? '0px'
          : '0px'
        : deviceType === 'tablet'
          ? '0px'
          : '0px',
      marginRight: usesCompactPurpleProfile ? '0px' : '2px',
      userSelect: 'none',
    }, // --- THIS IS THE OVERLAY STYLE ---
    // This overlay will cover the entire BetBoxControl when active
    disabledOverlay: {
      position: 'absolute',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0, 0, 0, 0.3)', // Black mask with 50% opacity
      borderRadius: usesCompactPurpleProfile ? '10px' : '20px', // Match the container's border radius
      zIndex: 10, // Ensure it's on top of other elements
      pointerEvents: 'auto', // Intercept all mouse/touch events
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      color: 'white',
      fontSize: '16px',
      fontWeight: 'bold',
    }, // --- END OVERLAY STYLE ---
    // Main Bet Box Container
    betBox: {
      position: 'relative',
      display: 'flex',
      height: '100%',
      maxHeight: usesCompactPurpleProfile ? '126px' : '184px',
      width: '100%',
      maxWidth: '384px',
      flexDirection: 'column',
      justifyContent: 'flex-start',
      alignItems: 'center',
      padding: '12px',
    }, // Bet/Auto Mode Tab Switch Area

    betBoxTabs: {
      height: '32px',
      width: usesCompactPurpleProfile ? '166px' : '208px',
      boxShadow: 'none',
      marginBottom: usesCompactPurpleProfile ? '24px' : undefined,

      // SKYX_NEW ONLY POSITION ADJUST
      ...(usesCompactPurpleProfile && {
        position: 'relative',
        top: '0px',
        left: '0px',
      }),
    },
    betModeToggle: {
      fontSize: '12px',
      marginBottom: usesCompactPurpleProfile ? undefined : '8px',
      display: 'flex',
      width: '100%',
      height: '32px',
      padding: usesCompactPurpleProfile ? '3px' : undefined,
      boxSizing: usesCompactPurpleProfile ? 'border-box' : undefined,
      gap: usesCompactPurpleProfile ? '6px' : undefined,
      background: frameBg,
      borderRadius: usesCompactPurpleProfile ? '999px' : '64px',
      // border: `solid 1px ${frameStroke}`,
      boxShadow: 'none',
    },

    betBoxTabSlider: {
      background: tabActiveBg,
      color: usesCompactPurpleProfile ? '#ffffff' : theme.colors.text,
      height: usesCompactPurpleProfile ? '100%' : '32px',
      top: usesCompactPurpleProfile ? '0px' : '0px',
      // left: usesCompactPurpleProfile ? '2.5px' : '0px',
      cursor: 'default',
      ...(usesCompactPurpleProfile ? { textTransform: 'none' as const } : {}),
    },

    betBoxTab: {
      padding: usesCompactPurpleProfile ? '0' : '2px 1px',
      height: usesCompactPurpleProfile ? '100%' : undefined,
      borderRadius: usesCompactPurpleProfile ? 14 : 20,
      background: 'transparent',
      border: 'none',
      color: tabInactiveColor,
      fontSize: usesCompactPurpleProfile ? 13 : 12,
      fontWeight: usesCompactPurpleProfile ? 700 : 450,
      cursor: 'pointer',
      textAlign: 'center',
      textTransform: usesCompactPurpleProfile ? ('none' as const) : undefined,
      zIndex: 1,
      position: 'relative',
      width: usesCompactPurpleProfile ? undefined : '100%',
      flex: usesCompactPurpleProfile ? '1 1 0' : undefined,
      fontFamily: usesCompactPurpleProfile ? "'Roboto', sans-serif" : uiFont,
      boxShadow: 'none',
      transition: 'background 0.6s ease, color 0.45s ease-in-out',
    }, // Middle section holding stake inputs and main bet button

    middleRow: {
      width: '100%',
      maxWidth: '390px',
      display: 'flex',
      flexDirection: 'row',
      justifyContent: 'center',
      alignItems: 'center',
      gap: '0px',
      paddingTop: usesCompactPurpleProfile ? 0 : '8px',
      margin: usesCompactPurpleProfile ? 0 : 'auto',
    },

    stakeColumn: {
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'center',
      alignItems: 'center',
      textAlign: 'center',
      width: usesCompactPurpleProfile ? quickBetGridWidth! : '140px',
      pointerEvents: 'auto',
      zIndex: 1,
    },
    stakeColumnMobile: {
      display: 'flex',
      flexDirection: 'column',
      gap: 6, // minWidth: 0,
      pointerEvents: 'auto',
      zIndex: 1,
    },

    stakeInputWrap: {
      height: '100%',
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      transition: ' opacity .1s',
      borderRadius: usesCompactPurpleProfile ? '999px' : '32px',
      backgroundColor: frameBg,
      fontWeight: '600',
      fontSize: 'inherit',
      lineHeight: '1',
      padding: usesCompactPurpleProfile ? '0 1px' : '4px',
      gap: usesCompactPurpleProfile ? '3px' : '2px',
      ...(usesCompactPurpleProfile && {
        backgroundColor: 'transparent',
        border: 'none',
      }),
    },
    stakeContainer: {
      height: usesCompactPurpleProfile ? '26px' : '32px',
      fontSize: '16px',
    },
    stakeInput: {
      flex: '1',
      minWidth: '0',
      maxWidth: usesCompactPurpleProfile ? '100px' : '80px',
      textAlign: 'center',
      background: usesCompactPurpleProfile ? '#1E0D38' : 'transparent',
      border: usesCompactPurpleProfile
        ? '1px solid rgba(123,60,255,0.5)'
        : 'none',
      borderRadius: usesCompactPurpleProfile ? '9px' : '0px',
      boxSizing: usesCompactPurpleProfile ? 'border-box' : undefined,
      color: usesCompactPurpleProfile ? '#ffffff' : theme.colors.text2,
      fontWeight: '800',
      fontSize: usesCompactPurpleProfile ? 17 : 15,
      outline: 'none',
      pointerEvents: 'auto',
      userSelect: 'text',
      cursor: 'text',
      zIndex: 0,
      padding: usesCompactPurpleProfile ? '0 4px' : '0 8px',
      fontFamily: inputFont,
      textRendering: 'geometricPrecision',
      transition: 'font-size 0.3s ease-in-out, letter-spacing 0.3s ease-in-out',
      fontVariantNumeric: 'proportional-nums', // <-- THE FIX

      lineHeight: usesCompactPurpleProfile ? 1 : 1.2,
      transform: usesCompactPurpleProfile ? 'scaleX(1.0)' : 'scaleX(1.2)',
      display: 'inline-block',
      WebkitFontSmoothing: 'antialiased',
      MozOsxFontSmoothing: 'grayscale',
      height: usesCompactPurpleProfile ? '24px' : 'auto',
      boxShadow: 'none',
      caretColor: usesCompactPurpleProfile ? '#ffffff' : undefined,
    }, // stakeInput: {
    //   width: '100%',
    //   textAlign: 'center',
    //   background: 'transparent',
    //   border: 'none',
    //   color: theme.colors.text2,
    //   fontWeight: '650',
    //   fontSize: 15,
    //   outline: 'none',
    //   pointerEvents: 'auto',
    //   userSelect: 'text',
    //   cursor: 'text',
    //   zIndex: 1,
    //   padding: '0 10px',
    //   fontFamily: '"Inter", "Roboto Mono", "Consolas", monospace',
    //   lineHeight: 1.2,
    //   transform: 'scaleX(1.2)',
    //   display: 'inline-block',
    //   WebkitFontSmoothing: 'antialiased',
    //   MozOsxFontSmoothing: 'grayscale',
    // },
    // Stake increment/decrement button
    stakeBtn: {
      width:
        deviceType === 'tablet' && !isIpad
          ? '3vw'
          : deviceType === 'desktop' && isIpad && !isLandscape
            ? '2.4vh'
            : usesCompactPurpleProfile
              ? '23px'
              : '23px',
      height:
        deviceType === 'tablet' && !isIpad
          ? '3vw'
          : deviceType === 'desktop' && isIpad && !isLandscape
            ? '2.4vh'
            : usesCompactPurpleProfile
              ? '23px'
              : '23px',
      borderRadius: '50%',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      background: usesCompactPurpleProfile
        ? 'linear-gradient(180deg, #5b2bbf 0%, #4b1d9e 100%)'
        : stakeBtnBg,
      border: usesCompactPurpleProfile ? '1px solid #9a65f7' : 'none',
      padding: 0,
      color: '#ffffff',
      fontSize: usesCompactPurpleProfile ? 13 : 18,
      fontWeight: 800,
      pointerEvents: 'auto',
      cursor: 'pointer',
      transition: 'all .15s ease',
      flexShrink: 0,
      boxShadow: usesCompactPurpleProfile
        ? 'inset 0 1px 0 rgba(255,255,255,0.18)'
        : 'none',
      zIndex: 2,
      position: 'relative',
    },

    stakeBtnDisabled: {
      opacity: 0.5,
      cursor: 'default',
    }, // Quick bet buttons (1/2, 2x, etc.)

    quickBetGrid: {
      display: 'grid',
      gridTemplateColumns: 'repeat(2, 1fr)',
      gap: quickBetGridGap,
      width: usesCompactPurpleProfile ? quickBetGridWidth : '95%',
      maxWidth: usesCompactPurpleProfile ? quickBetGridWidth : '140px',
      marginTop: usesCompactPurpleProfile ? '12px' : '4px',
      marginRight: '0px',
      marginBottom: usesCompactPurpleProfile ? '0px' : '0px',
    },

    quickBetBtn: {
      fontSize: usesCompactPurpleProfile ? quickBetChipFontSize : '14px',
      width: usesCompactPurpleProfile ? '100%' : '68px',
      height: usesCompactPurpleProfile ? 'auto' : '24px',
      lineHeight: '1',
      padding: usesCompactPurpleProfile ? quickBetChipPadding : '0',
      background: usesCompactPurpleProfile ? '#18073D' : quickBetBg,
      border: usesCompactPurpleProfile
        ? '1px solid rgba(123,60,255,0.35)'
        : `1px solid ${quickBetBorder}`,
      cursor: 'pointer',
      color: usesCompactPurpleProfile ? '#C9B8FF' : quickBetText,
      fontFamily: usesCompactPurpleProfile ? uiFont : undefined,
      fontWeight: 500,
      borderRadius: quickBetChipBorderRadius,
      textAlign: 'center',
      boxShadow: usesCompactPurpleProfile
        ? 'inset 0 1px 0 rgba(255,255,255,0.16), inset 0 -1px 0 rgba(0,0,0,0.25)'
        : 'none',
      boxSizing: usesCompactPurpleProfile ? 'border-box' : undefined,
    }, // Main Bet Button container

    mainBetContainer: {
      width: `calc(100% - ${quickBetGridWidth ?? '148px'} - 12px)`,
      marginLeft: '12px',
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      flexDirection: 'column',
      borderRadius: usesCompactPurpleProfile ? '12px' : '16px',
      border: `solid 1px ${frameStroke}`,
      background: usesCompactPurpleProfile
        ? 'linear-gradient(180deg, #330774 0%, #170550 100%)'
        : 'transparent',
    },

    mainBetBtn: {
      color: '#fafafa',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      flexDirection: 'column',
      width: '100%',
      height: '64%',
      minHeight: '80px',
      fontSize: usesCompactPurpleProfile ? '18px' : '22px',
      borderRadius: usesCompactPurpleProfile ? '12px' : '12px',
      cursor: 'pointer',
      border: usesCompactPurpleProfile ? '1px solid #73ff49' : '1px solid',
      boxShadow: usesCompactPurpleProfile
        ? 'inset 0 1px 0 rgba(255,255,255,0.25), inset 0 -2px 0 rgba(0,0,0,0.18)'
        : 'none',
      fontVariantNumeric: 'lining-nums tabular-nums',
    },

    mainBetBtnDisabled: {
      opacity: 0.6,
      cursor: 'default',
      filter: 'grayscale(0.3)',
      pointerEvents: 'none',
    },

    mainBetTopLabel: {
      fontSize: usesCompactPurpleProfile
        ? deviceType === 'mobile'
          ? '19px'
          : '18px'
        : deviceType === 'mobile'
          ? '20px'
          : '22px',
      lineHeight: 1.1,
      textAlign: 'center',
      marginTop: usesCompactPurpleProfile ? '1px' : '7px',
      marginBottom: usesCompactPurpleProfile ? '1px' : '4px',
      userSelect: 'none',
      fontFamily: uiFont,
      letterSpacing: usesCompactPurpleProfile ? '0.5px' : '0px',
      textTransform: usesCompactPurpleProfile
        ? ('uppercase' as const)
        : ('none' as const),
      fontWeight: usesCompactPurpleProfile ? '800' : '500',
      maxWidth: '100%',
      padding: '0 8px',
      boxSizing: 'border-box',
      wordBreak: 'break-word',
      overflowWrap: 'anywhere',
    },

    mainBetAmountLabel: {
      lineHeight: usesCompactPurpleProfile ? 1.2 : '25px',
      fontWeight: usesCompactPurpleProfile ? 700 : 400,
      marginTop: usesCompactPurpleProfile
        ? '2px'
        : deviceType === 'mobile'
          ? '8px'
          : '0px',
      textAlign: 'center',
      userSelect: 'none',
      fontSize: usesCompactPurpleProfile ? '13px' : '22px',
      fontVariantNumeric: 'lining-nums tabular-nums',
      fontFamily: uiFont,
      letterSpacing: usesCompactPurpleProfile ? '0.2px' : '0px',
      opacity: 1,
      color: usesCompactPurpleProfile ? '#ffffff' : undefined,
    }, // Auto Bet Inputs (Take Profit, Stop Loss, etc.)

    mainBetWaitingLabel: {
      fontSize: usesCompactPurpleProfile ? '12px' : '14px',
      lineHeight: usesCompactPurpleProfile ? 1.3 : 1.15,
      textAlign: 'center',
      userSelect: 'none',
      fontFamily: uiFont,
      fontWeight: 500,
      letterSpacing: usesCompactPurpleProfile ? '0.2px' : '0px',
      color: usesCompactPurpleProfile ? 'rgba(255,255,255,0.65)' : '#ffffff',
      marginTop: usesCompactPurpleProfile ? '4px' : undefined,
      whiteSpace: 'normal',
      maxWidth: '100%',
      padding: '0 10px',
      boxSizing: 'border-box',
      wordBreak: 'break-word',
      overflowWrap: 'anywhere',
    },

    autoBetContainer: {
      display: 'flex',
      width: '100%',
      maxWidth: '384px',
      flexDirection: 'column',
      justifyContent: 'center', // borderTop: '1px solid #141516',
      alignItems: 'center',
      marginTop: 0,
    },
    autoBetWrapper: {
      display: 'flex',
      width: '100%',
      maxWidth: usesCompactPurpleProfile ? '100%' : '390px',
      flexDirection: 'row',
      justifyContent: 'space-between',
      alignItems: 'center',
      height: usesCompactPurpleProfile ? 'auto' : '39px',
      color: usesCompactPurpleProfile ? '#b370c5' : '#ccc',
      padding: usesCompactPurpleProfile ? '0' : ' 0 8px',
      marginTop: deviceType === 'desktop' ? '0px' : '-5px',
    },
    // Horizontal line separator (retained from first file)
    borderLine: {
      height: '1px',
      background: usesCompactPurpleProfile ? '#3c2193' : '#141516',
      width: '100%',
      margin: deviceType === 'desktop' ? '' : '4px auto',
    },
    cashoutBlock: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: usesCompactPurpleProfile ? 'flex-start' : 'space-between',
      gap: usesCompactPurpleProfile ? 0 : undefined,
      margin: usesCompactPurpleProfile
        ? 0
        : deviceType === 'mobile'
          ? '0 2px'
          : '0 5px',
      maxWidth: usesCompactPurpleProfile ? '100%' : undefined,
    }, // Button to open AutoPlay settings
    autoPlayButton: {
      padding: deviceType === 'desktop' ? '4px 8px' : '3px 6px',
      fontSize: autoPlayPillFontSize,
      fontWeight: 400,
      color: theme.colors.text,
      backgroundColor: `${theme.colors.autoplayBtBg}`,
      border: `1px solid ${theme.colors.autoplayBtBgborder}`,
      borderRadius: autoPlayPillBorderRadius,
      cursor: 'pointer',
      transition: 'background-color 0.2s ease, color 0.2s ease',
      whiteSpace: 'normal',
      lineHeight: 1.15,
      width: 'auto',
      minWidth: autoPlayPillWidth,
      maxWidth: '100px',
      marginRight: deviceType === 'desktop' ? '0px' : '0px',
      position: 'relative',
      left: deviceType === 'desktop' ? '2px' : '1px',
      minHeight: autoPlayPillMinHeight,
      height: 'auto',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      textAlign: 'center',
      boxShadow: 'none',
      boxSizing: 'border-box',
      wordBreak: 'break-word',
      overflowWrap: 'anywhere',
    }, // New style added from the second file for hover effect

    autoPlayButtonHover: {
      backgroundColor: `${theme.colors.autoplayBgHover}`,
      borderColor: `${theme.colors.autoplayBgHover}`,
      marginTop: deviceType === 'tablet' ? '0px' : '0px',
    },

    autoLabel: {
      fontSize: '11px',
      lineHeight: 1.15,
      margin: usesCompactPurpleProfile ? '0' : '0 5px 0 0',
      textAlign: 'center',
      display: 'inline-block',
      maxWidth: '88px',
      whiteSpace: 'normal',
      wordBreak: 'break-word',
      overflowWrap: 'anywhere',
      flexShrink: 1,
    },
    cashoutInput: {
      height: '50%',
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      marginLeft: usesCompactPurpleProfile ? 0 : '15px', // width: '80px',
      fontSize: '12px',
      fontWeight: '500',
      backgroundColor: usesCompactPurpleProfile ? '#170550' : '#141516',
      borderRadius: '32px',
      width: ' 50%',
      padding: usesCompactPurpleProfile ? '0 8px' : '0px 5px',
      boxSizing: usesCompactPurpleProfile ? 'border-box' : undefined,
      boxShadow: 'none',
    },
    cashoutInputWrapper: {
      height: '100%',
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      transition: 'opacity .1s',
      fontWeight: '600',
      fontSize: 'inherit',
      lineHeight: '1',
      boxShadow: 'none',
    },
    autoInput: {
      width: '100%',
      border: '0',
      background: 'transparent',
      textAlign: usesCompactPurpleProfile ? 'left' : 'center',
      padding: '0',
      boxShadow: 'none',
      ...(usesCompactPurpleProfile
        ? {
            ...stakeAmountTextStyle,
            color: '#ffffff',
            caretColor: '#ffffff',
          }
        : {
            color: '#ccc',
            fontWeight: '600',
            letterSpacing: 'inherit',
            opacity: '0.5',
          }),
    },

    autoInputX: {
      // position: 'absolute',
      // right: '35px',
      opacity: '0.5',
      // pointerEvents: 'none',
      cursor: 'pointer',
      fontSize: usesCompactPurpleProfile ? '15px' : '18px',
      color: theme.colors.background4,
      boxShadow: 'none',
    }, // currencyStyle: {
    currencyStyle: {
      fontSize: usesCompactPurpleProfile ? '18px' : '18px',
      fontWeight: usesCompactPurpleProfile ? 800 : 400,
      marginLeft: '4px', // opacity: 1,
      fontFamily: uiFont,
      lineHeight: usesCompactPurpleProfile ? 1.05 : 'normal',
      letterSpacing: usesCompactPurpleProfile ? '0.5px' : '0px',
      color: usesCompactPurpleProfile ? '#ffffff' : undefined,
    },
  };
};
